/**
 * Provides basic rendering engine's APIs
 */
import React from 'react';
import ReactDOM from 'react-dom';
import clonedeep from 'lodash.clonedeep';
import { EventEmitter } from 'events';

import {
  getJoinKey,
  getUniqueKey,
  registerComponent,
  removeTailSlash,
  isFunction,
  isString,
  isObject,
  i18nTransform,
  parseQuery,
  arrayToHash,
  compose,
  isDev,
  isRenderTry,
  log,
  warn,
  error,
} from '@ali/re-utils';
import Loading from '@ali/vu-loading';
import Page from './Page';
import getContext from './apiUpgrade/getContext';

const APP_CONTAINER_ID_PREFIX = 'App';

const actionAPIs = [
  'getAction',
  'getStore',
  'getContainerId',
  'getCurrentPageId',
  'getCurrentPath',
  'setGlobal',
  'getGlobal',
  'getLocale',
  'setLocale',
  'reRender',
  'getDevice',
  'setLoading',
  'getDataPool',
  'onPageReady',
  'onDataReady',
  'onPageParamsChange',
  'onPageChange',
  'onPageLeave',
  'onPageEnter',
  'updatePage',
  'getFieldDataById',
  'getFieldData',
  'getSimpleFieldData',
  'resetFieldData',
  'getComponentInstance',
  'getComponentView',
  'execAction',
  'getNavData',
  'getCurrentPageNav',
  'i18n',
  'setDynamicProps',
  'getRouterBaseName',
  'getComponents',
];

const isHidden = (data = {}) => {
  data.params = data.params || {};
  return data.params.hidden;
};

class Context {
  static create = (options = {}) => new Context(options);

  /**
   * constructor
   * @param  {Object} options
   *   options.id               ContainerId, the unique ID of container
   *   options.schema           The schema format see
   *   options.locale
   *   options.routerBaseName
   *   options.routerParamsKey
   *   options.components
   *   options.dataPoolType
   *   options.pageMode
   *   options.globalProps
   *   options.device
   *   options.isRenderTry
   *   options.loading
   *   options.history
   *   options.isRenderNav
   *   options.isSectionalRender
   *   options.isSinglePage
   *     http://dip.alibaba-inc.com/pages/schemas/27860?spm=a312q.7764190.0.0.BNei5M
   *     or /test/schema/*.json
   *   options.isContextInAction
   */
  constructor(options) {
    this.schema = options.schema || options.dataSource; // 兼容
    this.saveRawSchema(this.schema);
    this.rawComponents = options.components || [];
    this.components = registerComponent(this.rawComponents);
    this.emitter = new EventEmitter();

    /* --------- Router Context ---------- */
    this.router = {};
    this.routerBaseName = removeTailSlash(options.routerBaseName || '');
    this.routerParamsKey = options.routerParamsKey || [];

    /* ---------- Context State ---------- */

    // the unique id of a container linked to this context
    // DOM ID
    this.containerId = options.id;

    /* ---------- Locale ---------- */
    this.locale = options.locale || 'en_US';

    this.currentPath = this.getDefaultPageId();
    this.currentPageId = this.getDefaultPageId();
    this.defaultPageId = this.getDefaultPageId();

    /* -------------- Stores ------------- */
    // components' global props
    // 不通过 UI 设置器来进行组件的全局配置
    this.globalProps = options.globalProps || {};

    this.globalDataPool = options.globalDataPool || [];
    this.globalEvents = options.globalEvents || [];

    // context's global data object
    // 外部存储全局数据
    this.globalData = {};

    // current container's data pool
    // 页面级数据源
    // @deprecated
    this.dataPool = {};

    this.pageStores = {};
    this.pageActions = {};
    this.dynamicProps = {};

    // dataPool type 'offline' 'online'
    this.dataPoolType = options.dataPoolType || 'offline';

    // 'OFFLINE' or 'ONLINE'
    this.pageMode = options.pageMode || 'ONLINE';

    // device 'DEFAULT' 'PC' 'MOBILE'
    this.device = options.device || 'DEFAULT';

    this.history = options.history || 'BROWSER';
    this.navigationName = options.navigationName || 'navigation';
    this.routerName = options.routerName || 'router';

    this.isRenderTry = options.isRenderTry || isDev() || isRenderTry();

    this.loading = options.loading || <Loading />;

    this.componentInstance = {};
    this.instanceData = {};
    this.builtinActions = [];
    this.isRenderNav = options.isRenderNav !== false; // default true
    this.isSectionalRender = options.isSectionalRender === true; // default false
    this.isSinglePage = options.isSinglePage === true;
    this.i18nTransformWithRaw = options.i18nTransformWithRaw === true;
    if (!('isContextInAction' in options)) {
      this.isContextInAction = true; // ctx 是否在 action 中
    } else {
      this.isContextInAction = options.isContextInAction;
    }

    // plugins holder
    this.fn = {};
    this.middlewares = [];

    if (!options.id) {
      this.containerId = getJoinKey(APP_CONTAINER_ID_PREFIX, getUniqueKey());
    }
    this.isUsingMock = options.isUsingMock === true;
  }

  get store() {
    return this.pageStores[this.currentPageId];
  }

  get dp() {
    return this.dataPool[this.currentPageId];
  }

  clearData() {
    this.dataPool = {};
    this.pageStores = {};
    this.pageActions = {};
    this.componentInstance = {};
    this.renderedPlugin = [];
    this.renderedComponent = [];
  }

  saveRawSchema(schema) {
    this.rawSchema = clonedeep(schema);
  }

  // --------------------------------
  // -- Middleware / Plugins Helper -
  // --------------------------------

  use(func, type = 'BEFORERENDER') {
    this.middlewares.push({ type, func });
    return this;
  }

  applyMiddleware(type = 'BEFORERENDER') {
    const middlewares = [];
    this.middlewares.forEach((middleware) => {
      if (isObject(middleware) && middleware.type === type) {
        middlewares.push(middleware.func);
      }
    });

    switch (type) {
      case 'BEFORERENDER':
        return compose(middlewares)(this.schema);
      case 'AFTERENDER':
        return compose(middlewares)(this.getCurrentPath());
      default:
        return compose(middlewares)(this.schema);
    }
  }

  makeFnWithContext(f) {
    const that = this;
    return function wrapper(...args) {
      // 优先使用函数调用时的this
      return f.call(this || that.getActionContext(), ...args, that);
    };
  }

  registerMiddleware(f, ...args) {
    return this.makeFnWithContext(f)(...args);
  }

  /**
   * 向 context 实例中注入插件引用
   * @param  {String | Object} pluginName
   *   ctx.registerPlugin('dialog', function() { return Dialog(); });
   *   ctx.registerPlugin('dialogs', { dialog: func, alert: func });
   *   ctx.registerPlugin({ dialog: dialogFn, alert: alertFn });
   * @param  {Function or Object} plugin
   * @param autoRun 自动执行
   */

  registerPlugin(pluginName, plugin, autoRun = false, injectContext = true) {
    if (isObject(pluginName)) {
      const plugins = pluginName;
      Object.keys(plugins).forEach((key) => {
        if (isFunction(plugins[key]) || isObject(plugins[key])) {
          this._registerPlugin(key, plugins[key], autoRun);
        } else {
          warn(`Invalid plugin: ${plugins[key]}.The plugin type should be a function or object.`);
        }
      });
      return undefined;
    }
    return this._registerPlugin(pluginName, plugin, autoRun, injectContext);
  }

  _registerPlugin(pluginName, plugin, autoRun = false, injectContext = true) {
    if (!isString(pluginName)) {
      warn(`Invalid pluginName: ${pluginName}.The pluginName type should be a string.`);
      return undefined;
    }
    if (!isFunction(plugin) && !isObject(plugin)) {
      warn(`Invalid plugin: ${plugin}.The plugin type should be a function or object.`);
      return undefined;
    }
    if (isObject(plugin)) {
      Object.keys(plugin).forEach((key) => {
        if (isFunction(plugin[key]) && injectContext) {
          plugin[key] = this.makeFnWithContext(plugin[key]);
        }
      });
    } else if (injectContext) {
      plugin = this.makeFnWithContext(plugin);
    }
    this.collectRenderedPlugin(`${pluginName}@${plugin.version || 'null'}`);

    if (autoRun) {
      return plugin();
    }
    this.fn[pluginName] = plugin;
    return undefined;
  }

  /**
   * register apis to action running context
   */
  registerActionAPIs(action) {
    const ctx = this;
    const APIs = {};
    actionAPIs.forEach((API) => {
      APIs[API] = ctx[API].bind(ctx);
    });
    action.addAPIs({
      ...APIs,
      fn: ctx.fn,
    });
  }

  i18n(key) {
    if (this.schema.i18nData && isObject(this.schema.i18nData)) {
      if (!isString(this.schema.i18nData[key])) {
        log(`I18nData couldn't find the key '${key}'.`);
        return '';
      }
      return this.schema.i18nData[key];
    }

    warn('I18nData is empty.');
    return '';
  }

  // --------------------------------
  // -------- Field Handler ---------
  // --------------------------------

  /**
   * 获取页面上所有表单的值,并自动校验
   * @param formId 表单 id
   * @param noValid 不做校验
   * @param simple 简洁版数据结构
   * @returns {{success: boolean, content: Array/Object, errorMsg: Array}}
   * @deprecated use store.getForm(formId).getFormData()
   */
  getFieldData(formId, noValid = false, simple = false) {
    const form = this.getStore().getForm(formId);

    if (!form) return null;

    if (!noValid) {
      form.forceValid();
    }

    let result = simple ? form.getSimpleFormData() : form.getFormData();
    if (noValid && !result.success) {
      result = {
        ...result,
        success: true,
        errorMsg: [],
      };
    }
    return result;
  }

  /**
   * 获取页面上所有表单的值的简洁版,并自动校验
   * @param formId 分组 id
   * @param noValid 不做校验
   * @returns {{success: boolean, content: Object, errorMsg: Array}}
   * @deprecated
   */
  getSimpleFieldData(formId = '', noValid = false) {
    return this.getFieldData(formId, noValid, true);
  }

  /**
   * 根据 fieldId 获取表单数据
   * @param fieldId
   * @param noValid 不做校验, 默认 true
   * @returns {object}
   * @deprecated use store.get(fieldId).getAsFormData();
   */
  getFieldDataById(fieldId, noValid = true) {
    const state = this.getStore().get(fieldId);
    if (!state || !state.isState) {
      return {};
    }
    if (!noValid) {
      state.forceValid();
    }
    const data = state.getAsFormData(true);
    return !data.hasError && noValid ? data.fieldData : {};
  }

  /**
   * 重置表单数据
   *
   * @param formId {stirng} 分组 id
   * @deprecated use store.getForm(formId).reset()
   */
  resetFieldData(formId = '') {
    const form = this.getStore().getForm(formId);
    if (form) {
      form.reset();
    }
    return this;
  }

  // --------------------------------
  // ------- schema transform -------
  // --------------------------------

  transformSchema(schema) {
    schema.i18nData = i18nTransform.i18nDataTransform(schema.i18nData);
    const s = i18nTransform(schema, this.getLocale(), schema.i18nData, this.i18nTransformWithRaw);
    if (s.instanceData) {
      if (isObject(s.instanceData)) { // 老数据
        s.instanceData = [s.instanceData];
      }
      s.instanceData = arrayToHash(s.instanceData, 'id');
    }

    this.setSchema(s, false);

    return s;
  }

  transformInstanceData(instanceData = {}) {
    // formData
    const formData = arrayToHash(instanceData.formData || [], 'fieldId');
    // flowData
    const flowData = instanceData.flowData || {};
    const behaviorData = arrayToHash(flowData.behaviors || [], 'fieldId');
    // propsData
    const propsData = arrayToHash(instanceData.propsData || [], 'fieldId');

    return {
      formData,
      flowData,
      behaviorData,
      propsData,
    };
  }

  // --------------------------------
  // ------ Setter / Getter ---------
  // --------------------------------

  setAction(pageId, action) {
    this.pageActions[pageId] = action;
    return this;
  }

  /**
   * Get current pageAction
   * @return {Object}
   */
  getAction(pageId) {
    return this.pageActions[pageId || this.currentPageId];
  }

  /**
   * Set pageStore with pageId
   * @param {String} pageId
   * @param {Object} store
   */
  setStore(pageId, store) {
    this.pageStores[pageId] = store;
    return this;
  }

  /**
   * Get current pageStore
   * @return {Object}
   */
  getStore(pageId) {
    return this.pageStores[pageId || this.currentPageId];
  }

  setDataPool(pageId, dataPool) {
    this.dataPool[pageId] = dataPool;
    return this;
  }

  /**
   * 获取当前上下文的唯一数据池
   * @return {Object}
   * @deprecated
   */
  getDataPool(pageId) {
    log('getDataPool is deprecated, try use store');
    return this.dataPool[pageId || this.currentPageId];
  }

  setCurrentPageId(pageId) {
    this.currentPageId = pageId;
    return this;
  }

  /**
   * 固定的 pageId, 从当前页面数据中获取，是固定的 pageId
   * 必须页面渲染未完成时再调用
   * @param {String} pageId
   */
  getCurrentPageId() {
    return this.currentPageId;
  }

  /**
   * path, 会根据 url 参数变化而变化，可能是 pageId、slug
   * @param {String} path
   */
  getCurrentPath() {
    return this.currentPath;
  }

  setCurrentPath(path) {
    this.currentPath = path;
    return this;
  }

  /**
   * Set components registry
   *
   * @param {Array} components registry
   */
  setComponents(components) {
    this.rawComponents = components;
    this.components = registerComponent(components);
    return this;
  }

  addComponents(components) {
    this.setComponents([...this.rawComponents, ...components]);
  }

  /**
   * Get components of current context
   *
   * @return {Object}
   */
  getComponents(isRaw = true) {
    return isRaw ? this.rawComponents : this.components;
  }

  /**
   * Get component View
   *
   * @returns {ReactClass}
   */
  getComponentView(componentName) {
    return this.components[componentName];
  }

  /**
   * Set current context's router
   *
   * @param {Object} router
   */
  setRouter(router) {
    this.router = router;
    return this;
  }

  /**
   * 设置路由 baseName
   * 该 `baseName` 为后端的真实请求路径, 最后一位不能带 '/'
   *
   * @param __basename {string}
   */
  setRouterBaseName(baseName) {
    this.routerBaseName = removeTailSlash(baseName);
    return this;
  }

  /**
   * Get router's basename
   *
   * @return {String}
   */
  getRouterBaseName() {
    return this.routerBaseName;
  }

  /**
   * Set Router's params keys
   * if you do not set the params keys when router jump to
   * a new route the keys which are not on the white-list
   * will be cleared.
   * @param {Array} keys
   */
  setRouterParamsKey(keys) {
    this.routerParamsKey = keys;
  }

  getRouterParamsKey() {
    return this.routerParamsKey;
  }

  setLoading(loading) {
    this.loading = loading;
  }

  getLoading() {
    return this.loading;
  }

  /**
   * Set global data
   * @param {Object} data
   */
  setGlobal(data) {
    this.globalData = {
      ...this.globalData,
      ...data,
    };
    return this;
  }

  /**
   * Get global data
   * @param  {String} key
   * @return {Mixed}
   */
  getGlobal(key) {
    return key ? this.globalData[key] : this.globalData;
  }

  /**
   * Get container id
   * @return {String}
   */
  getContainerId() {
    return this.containerId;
  }

  /**
   * @param dataPools
   * @param isMerge default true
   */
  setGlobalDataPool(dataPools = [], isMerge = true) {
    if (!isMerge) {
      this.globalDataPool = [];
    }
    const defaultOptions = {
      didFetch() {},
      exports: [],
      formUuid: '',
      id: '',
      isShared: true,
      method: 'GET',
      name: '',
      params: [],
      protocal: 'REMOTE',
      shouldFetch: true,
      title: '',
      url: '',
      willFetch() {},
    };
    dataPools.forEach((dataPool) => {
      const options = {
        ...defaultOptions,
        ...dataPool,
      };
      this.globalDataPool.push(options);
    });
    return this;
  }

  getGlobalDataPool() {
    return this.globalDataPool;
  }

  /**
   * {
   *   'fieldId': {
   *     onChange: [
   *       {
   *         name: "打开弹窗",
   *         id: "openDialog",
   *         params: {target: "dialog_iyqov545"},
   *         uuid: "1486438944642_1",
   *         func: 'function () {}',
   *       }
   *     ],
   *     onClick: [...]
   *   },
   *   'fieldId2': {...}
   * }
   * @param globalEvents
   * @returns {Context}
   */
  setGlobalEvents(globalEvents) {
    this.globalEvents = globalEvents;
    return this;
  }

  getGlobalEvents() {
    return this.globalEvents;
  }

  /**
   * 给组件全局设置 props
   * @param componentName
   * @param props
   */
  setGlobalProps(componentName, props) {
    this.globalProps[componentName] = props;
    return this;
  }

  /**
   * Get components' global props
   * @param  {String} componentName
   * @return {Object}
   */
  getGlobalProps(componentName) {
    return componentName ? (this.globalProps[componentName] || {}) : this.globalProps;
  }


  getBuiltinActions() {
    return this.builtinActions;
  }

  setBuiltinActions(actions) {
    this.builtinActions = actions;
  }

  appendSysActions(proxy) {
    const fns = Object.keys(this.fn || {});
    if ('util' in proxy || !fns.length) {
      return;
    }
    proxy.util = {};
    fns.forEach((action) => {
      proxy.util[action] = (...args) => this.fn[action](...args);
    });
    proxy.util.i18n = key => this.i18n(key);
    proxy.util.getLocale = () => this.getLocale();
  }

  mergeBuiltinActions(actions) {
    this.builtinActions = [...this.builtinActions, ...actions];
  }

  /**
   * 设置当前语言
   * @param locale
   */
  setLocale(locale) {
    this.locale = locale;
    return this;
  }

  /**
   * 获取当前语言
   * @returns {string}
   */
  getLocale() {
    return this.locale;
  }

  /**
   * 设置页面模式,有 OFFLINE 和 ONLINE
   * @param mode
   */
  setPageMode(mode) {
    this.pageMode = mode;
    return this;
  }

  getPageMode() {
    return this.pageMode;
  }

  useOfflineMode() {
    this.setPageMode('OFFLINE');
    return this;
  }

  /**
   * 默认的 pageId, 第一个非隐藏的页面 id
   * @returns {*}
   */

  getDefaultPageId() {
    if (this.defaultPageId || this.defaultPageId === null) {
      return this.defaultPageId;
    }
    const { pages } = this.schema;
    let defaultPageId = null;
    if (Array.isArray(pages) && pages.length) {
      pages.some((page) => {
        if (!isHidden(page)) {
          defaultPageId = page.id;
          return true;
        }
        return false;
      });
    }
    return defaultPageId;
  }

  /**
   * @deprecated
   */
  getCurrentNavId() {
    return null;
  }

  getDataPoolType() {
    return this.dataPoolType;
  }

  setDataPoolType(type) {
    this.dataPoolType = type;
    return this;
  }

  setRenderTry() {
    this.isRenderTry = true;
    return this;
  }

  getRenderTry() {
    return this.isRenderTry;
  }

  setDevice(device) {
    this.device = device;
    return this;
  }

  /**
   * 'PC' or 'MOBILE'
   * @returns {DEVICE_TYPE}
   */
  getDevice() {
    return this.device;
  }

  setNavigationName(navigationName) {
    this.navigationName = navigationName;
    return this;
  }

  getNavigationName() {
    return this.navigationName;
  }

  setRouterName(routerName) {
    this.routerName = routerName;
    return this;
  }

  getRouterName() {
    return this.routerName;
  }

  setSchema(schema, isSaveRaw = true) {
    this.schema = schema;
    if (isSaveRaw) {
      this.saveRawSchema(schema);
    }
  }

  getSchema(isRaw = true) {
    const { nav, ...schema } = isRaw ? this.rawSchema : this.schema;
    return {
      nav: { ...nav },
      ...schema,
    };
  }

  canRenderNav() {
    const { nav } = this.schema;
    const navName = this.navigationName;
    if (this.isRenderNav && !this.fn[navName]) {
      warn('Plugin `vu-navigation` is not found.');
    }
    const hasNavData = !!(nav && nav.data && nav.data.length);
    if (!hasNavData) {
      log('Nav data is empty.');
    }
    return !!(this.isRenderNav && this.fn[navName] && hasNavData);
  }

  setRenderNav(isRenderNav) {
    this.isRenderNav = isRenderNav;
  }

  setComponentInstance(key, instance) {
    if (key) {
      this.componentInstance[key] = instance;
    }
  }

  getComponentInstance(fieldId, iteratorId) {
    if (fieldId) {
      if (iteratorId) {
        return this.componentInstance[getJoinKey(iteratorId, fieldId)];
      }
      return this.componentInstance[fieldId];
    }
    return this.componentInstance;
  }

  setHistory(history) {
    this.history = history;
    return this;
  }

  /**
   * 'HASH' or 'BROWSER'
   * @returns {ROUTER_TYPE}
   */
  getHistory() {
    if (this.history !== 'BROWSER' && this.history !== 'HASH') {
      error('The history must be `BROWSER` or `HASH`');
    }
    return this.history;
  }

  /**
   * 必须在页面渲染完成后才能调用
   * @returns {*|{}}
   */
  getInstanceData() {
    const instanceData = this.getSchema(false).instanceData || {};
    return instanceData[this.getCurrentPageId()] || {};
  }

  setDynamicProps(dynamicProps) {
    this.dynamicProps = dynamicProps;
  }

  setImmediate(callback) {
    if (typeof callback === 'function') {
      return setTimeout(callback, 0);
    }
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        resolve(timer);
      }, 0);
    });
  }

  getNavData() {
    const { nav } = this.schema;
    return (nav || {}).data || [];
  }

  getCurrentPageNav() {
    const pageId = this.currentPageId;
    let navData = this.getNavData();
    const flattenNavData = (data) => {
      let crtRst = [];
      data.forEach((it) => {
        crtRst.push(it);
        if (it.children && it.children.length) {
          const childrenData = flattenNavData(it.children);
          crtRst = [...crtRst, ...childrenData];
        }
      });
      return crtRst;
    };
    navData = flattenNavData(navData);
    return navData.filter(it => it.navUuid === pageId)[0] || {};
  }

  // --------------------------------
  // ----- Actions of Context -------
  // --------------------------------

  execAction(actionName, ...args) {
    const action = this.getAction().actionsMap[actionName];
    if (action) {
      let ctx = args[0]; // 兼容老 API，第二个参数是 actionEngine 创建的 ctx
      if (!isObject(ctx) || !ctx.__createByAction && ctx !== this) { // 说明是新 API
        const APIs = {};
        actionAPIs.forEach((API) => {
          APIs[API] = this[API].bind(this);
        });
        ctx = { // 构造 actionEngine ctx
          router: this.router,
          fn: this.fn,
          dp: this.dp,
          store: this.store,
          ...APIs,
          ...this.dynamicProps,
        };
        args.unshift(ctx);
      }
      let context = this;
      if (this && typeof this.getActionContext === 'function') {
        context = this.getActionContext();
      }
      return action.invoke(context, args);
    }
    warn(`${actionName} is undefined`);
    return false;
  }

  execActionForDataPool(actionName, dataPool, ...args) {
    const context = this.getAction().createContextForDataPool(dataPool);
    return this.execActionWithContext(actionName, context, context, args);
  }

  /**
   * execute action in template
   * @param {String} actionName the action name
   * @param {Xprops} xprops which need to create context
   * @param  {...any} args arguments need by action
   */
  execActionWithXProps(actionName, xprops, ...args) {
    const { proxiedContext, context } = this.getAction().createAPIContext(xprops);
    context.setParams({
      args,
    });
    return this.execActionWithContext(actionName, context, proxiedContext, args);
  }

  execActionWithContext(actionName, context, proxiedContext, args) {
    const action = this.getAction().actionsMap[actionName];
    let actionArgs = args;
    if (context.isContextInAction) {
      actionArgs = [context, ...args];
    }
    return action.invoke(proxiedContext, actionArgs);
  }

  getPageSchema(pageId) {
    let pageSchema = null;
    this.schema.pages.some((page) => {
      if (page.id === (pageId || this.currentPageId)) {
        pageSchema = page.layout;
        return true;
      }
      return false;
    });
    return pageSchema;
  }

  getRenderedComponent() {
    return this.renderedComponent;
  }

  getRenderedPlugin() {
    return this.renderedPlugin;
  }

  setRenderedComponent(c) {
    this.renderedComponent = c;
  }

  collectRenderedComponent(c) {
    this.renderedComponent = this.renderedComponent || [];
    this.renderedComponent.push(c);
  }

  collectRenderedPlugin(p) {
    this.renderedPlugin = this.renderedPlugin || [];
    this.renderedPlugin.push(p);
  }

  reRender(options = {}) {
    this.clearData();
    this.destroyPage(); // react-router 自身不会二次更新,如果 schema.pages 发生了变化,想要路由发生变化,则需要销毁重新渲染.
    this.setSchema(options.schema || this.rawSchema, !!options.schema);
    this.render();
  }

  destroyPage() {
    const container = document.getElementById(this.containerId);
    ReactDOM.unmountComponentAtNode(container);
    return this;
  }

  /**
   * Update page with new schema
   * @param  {Object} schema
   */
  updatePage(schema) {
    this.emitter.emit('pageUpdate', schema);
    return this;
  }

  isVaildSchema(schema) {
    const valid = isObject(schema) && schema.pages;
    if (!valid) {
      error('The shcema is invalid.', schema);
    }
    return valid;
  }

  // --------------------------------
  // ------- Render Function --------
  // --------------------------------

  /**
   * Render function to initializely render a new page
   * under the body DOM Element.
   */
  render() {
    if (!this.schema || !this.schema.pages) {
      throw new Error('schema is required.');
    }

    let container = document.getElementById(this.containerId);
    if (!container) {
      container = document.createElement('div');
      container.id = this.containerId;
      document.body.appendChild(container);
    }
    log('=====> before render(exec middleware)');
    const schema = this.applyMiddleware('BEFORERENDER');
    if (this.isVaildSchema(schema)) {
      this.setSchema(schema);
    }
    ReactDOM.render(this.createElement(), container);
  }

  /**
   * 返回页面 DOM 结构
   * 抽离这个方法用于「多端渲染」
   * @returns {ReactElement}
   */
  createElement() {
    this.transformSchema(this.schema);
    if (!this.isSectionalRender && this.fn[this.routerName]) {
      log(`=====> renderNavigation, routerName: ${this.routerName}, ${!!this.fn[this.routerName]}`);
      return this.fn[this.routerName](Page);
    }
    if (this.isSectionalRender) {
      log('======> isSectionalRender');
    }
    return (
      <Page context={this} />
    );
  }

  findCurrentPageData(pageData = []) {
    let currentPageData = {};
    if (this.isSectionalRender) { // 局部渲染
      pageData.some((data) => {
        let pageId = parseQuery().___id; // 区分单页的 __id，有单页面里面嵌入局部渲染的场景
        if (Array.isArray(pageId)) {
          pageId = pageId.pop();
        }
        pageId = pageId || this.currentPageId;
        if (pageId && (pageId === data.id)) {
          this.setCurrentPageId(pageId);
          this.setCurrentPath(pageId);
          currentPageData = data;
          return true;
        }
        return false;
      });
    } else if (this.getDefaultPageId() === this.currentPath) { // 首页，查找第一个非隐藏的
      pageData.some((data) => {
        if (this.currentPath === data.id) {
          currentPageData = data;
          return true;
        }
        return false;
      });
    } else { // 异步请求的
      currentPageData = isHidden(pageData[0]) ? {} : pageData[0];
    }
    return currentPageData || {};
  }

  // --------------------------------
  // ------- Events Register --------
  // --------------------------------

  emitDataReady() {
    log('=====> dataReady');
    this.emitter.emit('dataReady');
  }

  emitPageReady() {
    log('%c=====> pageReady', 'color: green');
    this.emitter.emit('pageReady');
  }

  emitSchemaReady() {
    log('=====> schemaReady');
    this.emitter.emit('schemaReady');
  }

  onPageReady(cb) {
    this.emitter.on('pageReady', cb);
    return () => {
      this.emitter.removeListener('pageReady', cb);
    };
  }

  onDataReady(cb) {
    this.emitter.on('dataReady', cb);
    return () => {
      this.emitter.removeListener('dataReady', cb);
    };
  }

  onSchemaReady(cb) {
    this.emitter.on('schemaReady', cb);
    return () => {
      this.emitter.removeListener('schemaReady', cb);
    };
  }

  /**
   * 只有 spa 才会触发
   * @param cb
   * @returns {function()}
   */

  onPageParamsChange(cb) {
    this.emitter.on('pageParamsChange', cb);
    return () => {
      this.emitter.removeListener('pageParamsChange', cb);
    };
  }

  /**
   * @deprecated
   * 不会被触发了
   */
  onPageChange(cb) {
    this.emitter.on('pageChange', cb);
    return () => {
      this.emitter.removeListener('pageChange', cb);
    };
  }

  onPageEnter(cb) {
    this.emitter.on('pageEnter', cb);
    return () => {
      this.emitter.removeListener('pageEnter', cb);
    };
  }

  /**
   * 只有 spa 才会触发
   * @param cb
   * @returns {function()}
   */
  onPageLeave(cb) {
    this.emitter.on('pageLeave', cb);
    return () => {
      this.emitter.removeListener('pageLeave', cb);
    };
  }

  onPageUpdate(cb) {
    this.emitter.on('pageUpdate', cb);
    return () => {
      this.emitter.removeListener('pageUpdate', cb);
    };
  }

  setContextInAction(isContextInAction) {
    this.isContextInAction = isContextInAction;
  }

  getActionContext(props) {
    return getContext(this, props);
  }
}

export default Context;
